/**
 * Carrying a chosen Workflow Product through Google sign-in. A signed-out visitor who clicks "Start 7-day trial" is sent to sign in with
 * returnTo = the page they were on plus ?autoBuy=workflow:<product> (Event Suite: event_suite:<plan>). The product (or plan) name is the
 * only thing carried: no email, name or id. Back on the page, useAutoResumeCheckout reads the parameter, strips it and goes straight to
 * Stripe Checkout for that product. Same pattern as shared/apiPlanCarry.ts; the OAuth route validates returnTo with shared/returnTo.ts.
 */
export const WORKFLOW_BUY_IDS = ["book-writing-toolbox", "seller-studio", "legal-production-desk", "event-suite"] as const;
export type WorkflowBuyId = (typeof WORKFLOW_BUY_IDS)[number];
export type EventSuiteBuyPlan = "standard" | "premium";

/** What an autoBuy value resolves to: a monthly Workflow Product, or an Event Suite plan. */
export type WorkflowBuyTarget =
  | { kind: "workflow"; product: "book-writing-toolbox" | "seller-studio" | "legal-production-desk" }
  | { kind: "event_suite"; plan: EventSuiteBuyPlan };

const SUBSCRIPTION_IDS = ["book-writing-toolbox", "seller-studio", "legal-production-desk"] as const;

export function isWorkflowBuyId(v: unknown): v is WorkflowBuyId {
  return typeof v === "string" && (WORKFLOW_BUY_IDS as readonly string[]).includes(v);
}

/** The autoBuy value for a product click. Event Suite carries its plan (Standard unless told otherwise). */
export function autoBuyValueForWorkflow(product: WorkflowBuyId, eventPlan: EventSuiteBuyPlan = "standard"): string {
  return product === "event-suite" ? `event_suite:${eventPlan}` : `workflow:${product}`;
}

/** The relative returnTo for a click made on `pathname` (its own query kept, except any earlier autoBuy). */
export function returnToForWorkflow(product: WorkflowBuyId, pathname: string, search = "", eventPlan: EventSuiteBuyPlan = "standard"): string {
  const base = pathname.startsWith("/") && !pathname.startsWith("//") ? pathname : "/";
  const params = new URLSearchParams(search);
  params.set("autoBuy", autoBuyValueForWorkflow(product, eventPlan));
  return `${base}?${params.toString()}`;
}

/** What an autoBuy value such as "workflow:seller-studio" or "event_suite:premium" names, or null for anything else. */
export function workflowFromAutoBuy(autoBuy: string | null | undefined): WorkflowBuyTarget | null {
  if (!autoBuy) return null;
  if (autoBuy.startsWith("workflow:")) {
    const p = autoBuy.slice("workflow:".length);
    return (SUBSCRIPTION_IDS as readonly string[]).includes(p) ? { kind: "workflow", product: p as (typeof SUBSCRIPTION_IDS)[number] } : null;
  }
  if (autoBuy.startsWith("event_suite:")) {
    const p = autoBuy.slice("event_suite:".length);
    return p === "standard" || p === "premium" ? { kind: "event_suite", plan: p } : null;
  }
  return null;
}
